import React from 'react'

import { useTaskAPI } from '../TaskAPIProvider'
import { useTreeState, TYPES } from '../TreeStateProvider'
import useUIPagination from '../../hooks/useUIPagination'
import useTaskColumnSettings from '../../hooks/useTaskColumnSettings'

const PAGE_SIZE = 25

const ControlledTaskTableStateContext = React.createContext(null)

const ControlledTaskTableStateProvider = ({ name, tasks, children }) => {
  const { formatTask } = useTaskAPI()
  const {
    state: treeState,
    dispatch: treeDispatch,
    Actions: TreeActions
  } = useTreeState(TYPES.TASK)

  const formattedTasks = React.useMemo(() => {
    if (!tasks) {
      return []
    }

    return tasks.data.map(formatTask)
  }, [tasks, formatTask])

  const {
    visibleElems: visibleTasks,
    updatePagination,
    ...pagination
  } = useUIPagination(formattedTasks, PAGE_SIZE, name, true)
  const columnSettings = useTaskColumnSettings(name)

  const [expanded, setExpanded] = React.useState(!Boolean(treeState[name]))
  const [searchFilter, setSearchFilter] = React.useState('')

  const toggleExpanded = React.useCallback(() => setExpanded(old => !old), [])

  React.useEffect(() => {
    treeDispatch({ type: TreeActions.UPDATE, id: name, open: !expanded })
  }, [expanded, name, treeDispatch, TreeActions.UPDATE])

  React.useEffect(() => {
    if (tasks) {
      updatePagination(tasks.pagination)
    }
  }, [updatePagination, tasks])

  return (
    <ControlledTaskTableStateContext.Provider
      value={{
        // Base table values
        tasks: visibleTasks,
        expanded,
        toggleExpanded,
        name,
        searchFilter,
        setSearchFilter,
        id: null,
        canAdd: false,
        key: name,

        // Pagination props
        ...pagination,

        // Column Setting props
        ...columnSettings
      }}
    >
      {children}
    </ControlledTaskTableStateContext.Provider>
  )
}

export default ControlledTaskTableStateProvider

export const useControlledTaskTableState = () =>
  React.useContext(ControlledTaskTableStateContext)
